import { supabase } from '@/lib/supabase';
import {
  transformRow,
  matchParticipant,
  duplicateKey,
  normalizeName,
  toMinorUnits,
  getMinorUnitsSync,
  type ImportColumnMapping,
  type ImportMember,
  type ParsedRow,
  type ParticipantMatchType,
} from '@sharecost/shared';
import { buildExpenseRows, fetchGroupExpenses } from './expenses';
import { fetchCategories } from './categories';

export interface ParsedFile {
  headers: string[];
  rows: Record<string, string>[];
}

export interface TokenResolution {
  token: string;
  displayLabel: string;
  memberId: string | null;
  matchType: ParticipantMatchType;
  /** When unmatched, whether to create a virtual member on commit (default ON). */
  createVirtual: boolean;
}

export interface ImportDraft {
  index: number;
  row: ParsedRow;
  duplicate: boolean;
  /** Whether this row will be imported (user-toggleable). */
  include: boolean;
  /** Resolved category — from the source column or AI-assigned. */
  categoryId: string | null;
  categoryLabel: string | null;
}

export interface ImportPreview {
  mapping: ImportColumnMapping;
  drafts: ImportDraft[];
  tokens: TokenResolution[];
}

export interface CommitContext {
  groupId: string;
  createdBy: string;
  groupCurrency: string;
  members: ImportMember[];
}

export interface CommitResult {
  expenses: number;
  settlements: number;
  failures: { index: number; error: string }[];
}

/** Rows sent to the AI for header detection + column mapping. */
const SAMPLE_ROW_COUNT = 6;
/** Min confidence to accept an AI-suggested category. */
const CATEGORY_CONFIDENCE_THRESHOLD = 0.6;
/** Max items per categorization Edge Function call. */
const CATEGORIZE_BATCH_SIZE = 80;

/**
 * Decode a file to text honoring its byte-order mark. Dlužníček (and other apps)
 * export CSV as UTF-16; `File.text()` would assume UTF-8 and produce garbage.
 */
function decodeTextFile(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  if (bytes[0] === 0xff && bytes[1] === 0xfe) return new TextDecoder('utf-16le').decode(buf);
  if (bytes[0] === 0xfe && bytes[1] === 0xff) return new TextDecoder('utf-16be').decode(buf);
  return new TextDecoder('utf-8').decode(buf);
}

/**
 * Parse a CSV or Excel file into a raw cell matrix (trimmed, empty rows dropped).
 * Whether the first row is a header is decided later by the AI (`hasHeader`).
 * Parsing libraries are loaded dynamically so they stay out of the main bundle.
 */
export async function parseSpreadsheet(file: File): Promise<string[][]> {
  const ext = file.name.split('.').pop()?.toLowerCase();
  let matrix: string[][];

  if (ext === 'csv' || file.type.includes('csv')) {
    const Papa = (await import('papaparse')).default;
    const text = decodeTextFile(await file.arrayBuffer());
    const res = Papa.parse<string[]>(text, { header: false, skipEmptyLines: true });
    matrix = (res.data ?? []).map((row) => row.map((c) => String(c ?? '').trim()));
  } else {
    const XLSX = await import('xlsx');
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    const sheet = wb.Sheets[wb.SheetNames[0]];
    const raw = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: '' });
    matrix = raw.map((row) => row.map((c) => String(c ?? '').trim()));
  }

  // Drop fully-empty rows.
  return matrix.filter((row) => row.some((c) => c.length > 0));
}

/**
 * Turn the raw matrix into header + keyed rows. When the file has no header,
 * columns are named positionally ("Column 1", ...) to match the AI's mapping.
 */
export function resolveRows(matrix: string[][], hasHeader: boolean): ParsedFile {
  if (matrix.length === 0) return { headers: [], rows: [] };

  const width = matrix.reduce((max, r) => Math.max(max, r.length), 0);
  const headers = hasHeader
    ? matrix[0].map((h, i) => h || `Column ${i + 1}`)
    : Array.from({ length: width }, (_, i) => `Column ${i + 1}`);
  const dataRows = hasHeader ? matrix.slice(1) : matrix;

  const rows = dataRows.map((arr) => {
    const obj: Record<string, string> = {};
    headers.forEach((h, i) => {
      obj[h] = arr[i] ?? '';
    });
    return obj;
  });

  return { headers, rows };
}

/** Call the Edge Function to detect the column mapping (and header presence). */
export async function detectImportMapping(
  matrix: string[][],
  members: ImportMember[],
): Promise<ImportColumnMapping> {
  const { data, error } = await supabase.functions.invoke('parse-import-mapping', {
    body: {
      sampleRows: matrix.slice(0, SAMPLE_ROW_COUNT),
      groupMembers: members.map((m) => ({ name: m.displayName, email: m.email })),
    },
  });

  if (error) throw error;
  if (data?.error) throw new Error(data.error);
  return data.mapping as ImportColumnMapping;
}

/** Description actually written to the DB — kept identical across preview and commit. */
function effectiveDescription(row: ParsedRow): string {
  return row.description || row.categoryName || 'Imported expense';
}

/** Normalize a timestamp to second precision (`YYYY-MM-DDTHH:MM:SS`) for comparison. */
function normalizeTimestamp(ts: string): string {
  return ts.replace(' ', 'T').slice(0, 19);
}

function existingExpenseKey(description: string, amountMinor: number, dateIso: string): string {
  return [normalizeName(description), amountMinor, normalizeTimestamp(dateIso)].join('|');
}

async function fetchExistingKeys(groupId: string): Promise<Set<string>> {
  const expenses = await fetchGroupExpenses(groupId);
  const keys = new Set<string>();
  for (const e of expenses) {
    keys.add(existingExpenseKey(e.description, Number(e.amount_minor_src), e.expense_date ?? ''));
  }
  return keys;
}

/**
 * Build the in-memory import preview: transform every row, resolve participant
 * tokens to members (or flag for virtual creation), and flag duplicates both
 * within the file and against existing group expenses.
 */
export async function buildImportPreview(
  parsed: ParsedFile,
  mapping: ImportColumnMapping,
  members: ImportMember[],
  groupId: string,
  categories: { id: string; name: string }[],
): Promise<ImportPreview> {
  const existingKeys = await fetchExistingKeys(groupId);
  const seenInFile = new Set<string>();
  const categoryByName = buildCategoryMap(categories);
  const categoryNameById = new Map(categories.map((c) => [c.id, c.name]));

  const drafts: ImportDraft[] = parsed.rows.map((rawRow, index) => {
    const row = transformRow(rawRow, mapping);

    const withinFileKey = duplicateKey({
      description: row.description,
      amount: row.amount,
      dateIso: row.dateIso,
      paidByToken: row.paidByToken,
      splitTokens: row.splits.map((s) => s.token),
    });
    let duplicate = seenInFile.has(withinFileKey);
    seenInFile.add(withinFileKey);

    // Only expenses are checked against existing expenses (settlements live elsewhere).
    if (!duplicate && row.kind === 'expense' && row.amount !== null && row.currencyCode) {
      const minor = toMinorUnits(row.amount, getMinorUnitsSync(row.currencyCode));
      const existKey = existingExpenseKey(effectiveDescription(row), minor, row.dateIso ?? '');
      if (existingKeys.has(existKey)) duplicate = true;
    }

    const isIncome = row.type === 'income';
    const hasBlocking =
      row.amount === null ||
      !row.currencyCode ||
      // 'none' rows legitimately have no per-row splits — they're split among all.
      (row.splits.length === 0 && mapping.splitStyle !== 'none');
    // Duplicates default to NOT imported but the user can still opt them in.
    const include = !duplicate && !isIncome && !hasBlocking;

    // Free first pass: match the source category column by name.
    const sourceCategoryId = row.categoryName
      ? categoryByName.get(normalizeName(row.categoryName)) ?? null
      : null;

    return {
      index,
      row,
      duplicate,
      include,
      categoryId: sourceCategoryId,
      categoryLabel: sourceCategoryId ? categoryNameById.get(sourceCategoryId) ?? null : null,
    };
  });

  // Unique participant tokens across the whole file.
  const tokenSet = new Map<string, string>();
  for (const d of drafts) {
    for (const s of d.row.splits) tokenSet.set(s.token, s.token);
    if (d.row.paidByToken) tokenSet.set(d.row.paidByToken, d.row.paidByToken);
  }

  const tokens: TokenResolution[] = [...tokenSet.values()].map((token) => {
    const match = matchParticipant(token, members);
    return {
      token,
      displayLabel: token.includes('@') ? token.split('@')[0] : token,
      memberId: match.memberId,
      matchType: match.matchType,
      createVirtual: match.memberId === null,
    };
  });

  // Second pass: AI-categorize the rows that will be imported but still lack a
  // category. Best-effort — failure here must not block the import.
  try {
    await applyAiCategories(drafts, categories, categoryNameById);
  } catch (e) {
    console.warn('AI categorization skipped:', e);
  }

  return { mapping, drafts, tokens };
}

function buildCategoryMap(categories: { id: string; name: string }[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const c of categories) map.set(normalizeName(c.name), c.id);
  return map;
}

interface CategoryAssignment {
  id: string;
  categoryId: string | null;
  confidence: number;
}

/** Call the categorization Edge Function in bounded batches. */
async function categorizeBatched(
  items: { id: string; description: string; amount: number | null; currency: string | null }[],
  categories: { id: string; name: string }[],
): Promise<CategoryAssignment[]> {
  const out: CategoryAssignment[] = [];
  for (let i = 0; i < items.length; i += CATEGORIZE_BATCH_SIZE) {
    const chunk = items.slice(i, i + CATEGORIZE_BATCH_SIZE);
    const { data, error } = await supabase.functions.invoke('categorize-expenses', {
      body: { items: chunk, categories },
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    if (Array.isArray(data?.assignments)) out.push(...(data.assignments as CategoryAssignment[]));
  }
  return out;
}

/** Mutate drafts in place with AI-suggested categories (high confidence only). */
async function applyAiCategories(
  drafts: ImportDraft[],
  categories: { id: string; name: string }[],
  categoryNameById: Map<string, string>,
): Promise<void> {
  const needing = drafts.filter(
    (d) => d.include && d.row.kind === 'expense' && !d.categoryId,
  );
  if (needing.length === 0 || categories.length === 0) return;

  const items = needing.map((d) => ({
    id: String(d.index),
    description: effectiveDescription(d.row),
    amount: d.row.amount,
    currency: d.row.currencyCode,
  }));

  const assignments = await categorizeBatched(items, categories);
  const byId = new Map(assignments.map((a) => [a.id, a]));

  for (const d of needing) {
    const a = byId.get(String(d.index));
    if (
      a?.categoryId &&
      a.confidence >= CATEGORY_CONFIDENCE_THRESHOLD &&
      categoryNameById.has(a.categoryId)
    ) {
      d.categoryId = a.categoryId;
      d.categoryLabel = categoryNameById.get(a.categoryId) ?? null;
    }
  }
}

/**
 * Build all rows client-side (resolving members, creating virtual-member rows,
 * computing splits) and commit them via a single atomic RPC. The RPC suppresses
 * per-row activity/notification triggers and writes one summary activity, so a
 * bulk import doesn't flood the feed or notify co-members per expense.
 */
export async function commitImport(
  preview: ImportPreview,
  ctx: CommitContext,
  onProgress?: (done: number, total: number) => void,
): Promise<CommitResult> {
  const tokenToId = new Map<string, string>();
  const newUsers: { id: string; display_name: string }[] = [];
  const newMembers: { id: string; user_id: string }[] = [];

  // Resolve tokens; virtual members get a client-generated id and are created
  // inside the RPC transaction (so their "joined" activity is suppressed too).
  for (const t of preview.tokens) {
    if (t.memberId) {
      tokenToId.set(t.token, t.memberId);
    } else if (t.createVirtual) {
      const id = crypto.randomUUID();
      tokenToId.set(t.token, id);
      newUsers.push({ id, display_name: t.displayLabel });
      newMembers.push({ id: crypto.randomUUID(), user_id: id });
    }
  }

  // All members after this import (existing + freshly created) — used to split
  // rows that carry no participant info ('none' style) equally among everyone.
  const allMemberIds = [
    ...new Set([...ctx.members.map((m) => m.id), ...newUsers.map((u) => u.id)]),
  ];
  const nameById = new Map<string, string>([
    ...ctx.members.map((m) => [m.id, m.displayName] as const),
    ...newUsers.map((u) => [u.id, u.display_name] as const),
  ]);

  const toImport = preview.drafts.filter((d) => d.include);
  const failures: CommitResult['failures'] = [];
  const expenseRows: Record<string, unknown>[] = [];
  const splitRows: Record<string, unknown>[] = [];
  const settlementRows: Record<string, unknown>[] = [];

  for (const draft of toImport) {
    const { row } = draft;
    try {
      const paidById = row.paidByToken ? tokenToId.get(row.paidByToken) : undefined;
      if (!paidById) throw new Error('Unresolved payer');

      const currencyCode = row.currencyCode || ctx.groupCurrency;

      if (row.kind === 'settlement') {
        const recipientSplit =
          row.splits.find((s) => tokenToId.get(s.token) !== paidById) ?? row.splits[0];
        const toUserId = recipientSplit ? tokenToId.get(recipientSplit.token) : undefined;
        if (!toUserId) throw new Error('Unresolved settlement recipient');

        settlementRows.push({
          id: crypto.randomUUID(),
          from_user_id: paidById,
          to_user_id: toUserId,
          amount_minor: toMinorUnits(row.amount as number, getMinorUnitsSync(currencyCode)),
          currency_code: currencyCode,
          description: row.description || 'Imported settlement',
          settlement_date: row.dateIso ?? new Date().toISOString(),
          created_at: new Date().toISOString(),
        });
      } else if (preview.mapping.splitStyle === 'none') {
        // No split info in the file → split equally among all group members.
        if (allMemberIds.length === 0) throw new Error('Group has no members');
        const participantDisplayNames: Record<string, string> = {};
        for (const id of allMemberIds) participantDisplayNames[id] = nameById.get(id) ?? '';

        const { expense, splits } = await buildExpenseRows({
          groupId: ctx.groupId,
          createdBy: ctx.createdBy,
          paidBy: paidById,
          description: effectiveDescription(row),
          amount: row.amount as number,
          currencyCode,
          categoryId: draft.categoryId ?? undefined,
          date: row.dateIso ?? undefined,
          splitType: 'equal',
          splitWith: allMemberIds,
          participantDisplayNames,
        });
        expenseRows.push(expense);
        splitRows.push(...splits);
      } else {
        const splitWith: string[] = [];
        const exactAmounts: Record<string, number> = {};
        const participantDisplayNames: Record<string, string> = {};

        for (const s of row.splits) {
          const id = tokenToId.get(s.token);
          if (!id) throw new Error(`Unresolved participant: ${s.token}`);
          splitWith.push(id);
          exactAmounts[id] = (exactAmounts[id] ?? 0) + s.amount;
          const resolved = preview.tokens.find((t) => t.token === s.token);
          participantDisplayNames[id] = resolved?.displayLabel ?? s.token;
        }

        const { expense, splits } = await buildExpenseRows({
          groupId: ctx.groupId,
          createdBy: ctx.createdBy,
          paidBy: paidById,
          description: effectiveDescription(row),
          amount: row.amount as number,
          currencyCode,
          categoryId: draft.categoryId ?? undefined,
          date: row.dateIso ?? undefined,
          splitType: 'exact',
          splitWith,
          exactAmounts,
          participantDisplayNames,
        });
        expenseRows.push(expense);
        splitRows.push(...splits);
      }
    } catch (err) {
      failures.push({
        index: draft.index,
        error: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  }

  const summaryCount = expenseRows.length + settlementRows.length;
  if (summaryCount === 0) {
    return { expenses: 0, settlements: 0, failures };
  }

  const { data, error } = await supabase.rpc('import_transactions', {
    p_group_id: ctx.groupId,
    p_users: newUsers,
    p_members: newMembers,
    p_expenses: expenseRows,
    p_splits: splitRows,
    p_settlements: settlementRows,
    p_summary_count: summaryCount,
  });
  if (error) throw error;

  onProgress?.(summaryCount, summaryCount);
  return {
    expenses: (data?.expenses as number) ?? expenseRows.length,
    settlements: (data?.settlements as number) ?? settlementRows.length,
    failures,
  };
}

export async function fetchImportCategories(): Promise<{ id: string; name: string }[]> {
  const cats = await fetchCategories();
  return cats.map((c) => ({ id: c.id, name: c.name }));
}
